<template>
	<div class="env-widget-container" @click="handleClick($event)">
		<div class="env-widget-header">
			<div class="env-header-meta">
				<h1 class="fc-dark text-ellipsis">
					{{ title }}
				</h1>
				<h6 v-if="subTitle" class="fw-normal fc-light text-ellipsis">
					{{ subTitle }}
				</h6>
			</div>
			<div class="env-widget-settings-icon">
				<Icon name="settings" type="filled" size="small" @click="settings($event)" />
			</div>
		</div>
		<div class="env-widget-body">
			<!-- @slot Used to display env widget meta data -->
			<slot />
		</div>
		<div class="env-widget-footer">
			<div class="env-footer-meta">
				<div class="env-footer-label fc-light">
					<h6 class="fw-normal fc-light upper-case text-ellipsis">
						{{ footerLabel }}
					</h6>
				</div>
				<div class="env-footer-toggle">
					<Icon
						:name="'menu-down'"
						type="filled"
						size="small"
						:rotate="innerFooterExpanded ? 0 : 180"
						@click="toggleFooter($event)"
					/>
				</div>
			</div>
			<div class="env-app-grid" :compact="!innerFooterExpanded">
				<!-- @slot Used to display environments -->
				<slot name="footer" />
			</div>
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Icon from "./../Icon/Icon";

export default defineComponent({
	name: "EnvironmentWidget",
	components: {
		Icon
	},
	props: {
		/**
		 * Title of Environment
		 */
		title: {
			type: String,
			required: true
		},
		/**
		 * Sub-Title of Environment (Optional)
		 */
		subTitle: {
			type: String,
			default: null
		},
		/**
		 * true |  false
		 */
		showSettingsIcon: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		footerExpanded: {
			type: Boolean,
			default: true
		},
		/**
		 * Environment Footer label
		 */
		footerLabel: {
			type: String,
			default: null
		}
	},
	computed: {
		innerFooterExpanded: {
			// getter
			get() {
				return this.footerExpanded;
			},
			// setter
			set(newValue) {
				/**
				 * Whenever footerExpanded is updated.<br/>
				 * use 'footerExpanded.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:footerExpanded
				 */
				this.$emit("update:footerExpanded", newValue);
			}
		}
	},
	methods: {
		toggleFooter(e) {
			e.stopPropagation();
			this.innerFooterExpanded = !this.innerFooterExpanded;
			/**
			 * toggleFooter event : Trigerred when footer is toggled.
			 *
			 * @event toggleFooter
			 */
			this.$emit("toggleFooter", this.innerFooterExpanded, e);
		},
		handleClick(e) {
			/**
			 * Click event
			 *
			 * @event click
			 */
			this.$emit("click", e);
		},
		settings(e) {
			e.stopPropagation();
			/**
			 * Click on settings icon
			 *
			 * @event settings
			 */
			this.$emit("settings", e);
		}
	}
});
</script>
<style lang="scss">
div.env-widget-container {
	display: flex;
	flex-direction: column;
	border-radius: 8px;
	cursor: pointer;
	position: relative;
	// min-width: 411px;
	// max-width: 300px;
	overflow: hidden;
	background-color: var(--dashboard-element-light);

	> div.env-widget-header {
		padding: 16px;
		display: flex;
		min-height: 78px;
		> * + * {
			margin-left: 16px;
		}

		border-bottom: 1px solid var(--dashboard-border);

		> div.env-widget-settings-icon {
			margin-left: auto;
			padding-left: 8px;
		}

		> div.env-header-meta {
			> * {
				white-space: normal;
				word-break: break-all;
			}
		}

		// > .flow-picto-container {
		// 	flex: 1 0 auto;
		// }
	}

	> div.env-widget-body {
		padding: 16px 16px 20px 16px;
		display: flex;
		flex-direction: column;

		> * {
			min-width: 150px;
		}

		> * + * {
			margin-top: 12px;
		}

		> div.env-stat {
			> * + * {
				margin-top: 4px;
			}
		}

		border-bottom: 1px solid var(--dashboard-border);
	}
	> div.env-widget-footer {
		padding: 16px;
		display: flex;
		flex-direction: column;

		> div.env-footer-meta {
			display: flex;
			justify-content: space-between;
			align-items: center;
			// padding-bottom: 16px;

			> div.env-footer-toggle {
				display: inline-flex;
				align-items: center;
			}

			> div.env-footer-label {
				> * {
					white-space: normal;
					word-break: break-all;
				}
			}
		}

		> * + * {
			margin-top: 12px;
		}

		> div.env-app-grid {
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
			grid-gap: 4px;
			grid-auto-rows: 1fr;

			&[compact] {
				grid-template-columns: repeat(auto-fill, minmax(24px, 1fr));
			}
		}
	}

	// &:active,
	// &:hover:not(:active) {
	// 	&:before {
	// 		content: "";
	// 		position: absolute;
	// 		top: 0;
	// 		right: 0;
	// 		bottom: 0;
	// 		left: 0;
	// 		width: 100%;
	// 		height: 100%;
	// 		background-color: var(--dashboard-font-dark);
	// 		border-radius: 8px;
	// 	}
	// }

	// &:active {
	// 	&:before {
	// 		opacity: 0.08;
	// 		cursor: pointer;
	// 	}
	// }

	// &:hover:not(:active) {
	// 	&:before {
	// 		opacity: 0.04;
	// 	}
	// }

	&:active {
		filter: contrast(90%);
	}

	&:hover:not(:active) {
		filter: contrast(95%);
	}
	&:focus {
		box-shadow: inset 0 0 0 2px var(--dashboard-primary);
		outline: none;
	}
}

div.flow-env-widget-grid {
	display: grid;
	grid-gap: 12px;
	grid-template-columns: repeat(auto-fill, 300px);
	align-items: flex-start;
	// grid-auto-rows: 1fr;
	> div.flow-new-placeholder {
		min-height: 291px;
	}
}
</style>
